/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect, useContext } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { DirectionContext } from "../App";
import API_ENDPOINTS from "../config/api";
import Cookies from "js-cookie";

const StatusSignaturePadPage = () => {
  // const { navigateTo } = useContext(DirectionContext);
  const navigate = useNavigate();
  const { applicationNumber } = useParams();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const lastPointRef = useRef({ x: 0, y: 0 });
  const totalPathLengthRef = useRef(0);
  const MIN_SIGNATURE_LENGTH = 300; // 최소 서명 길이 (픽셀 단위) - 이름 석자 정자 서명을 위한 최소 길이

  // 페이지 로드 시 스크롤 위치를 맨 위로 초기화
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, []);

  // 캔버스 초기화 및 구분선 그리기
  useEffect(() => {
    const drawDividers = () => {
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");

        // 캔버스 크기 설정 (고해상도 디스플레이 대응)
        const rect = canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;

        // 실제 캔버스 크기 설정
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;

        // CSS 크기는 표시 크기로 유지
        canvas.style.width = rect.width + "px";
        canvas.style.height = rect.height + "px";

        // 스케일 조정
        ctx.scale(dpr, dpr);

        // 그리기 설정
        ctx.strokeStyle = "#000";
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";

        // 구분선 그리기 (3등분) - 표시 크기 기준
        ctx.strokeStyle = "#e5e5e5";
        ctx.lineWidth = 1;
        const thirdWidth = rect.width / 3;
        const padding = 30; // 위아래 여백
        ctx.beginPath();
        ctx.moveTo(thirdWidth, padding);
        ctx.lineTo(thirdWidth, rect.height - padding);
        ctx.moveTo(thirdWidth * 2, padding);
        ctx.lineTo(thirdWidth * 2, rect.height - padding);
        ctx.stroke();

        // 그리기 설정 복원
        ctx.strokeStyle = "#000";
        ctx.lineWidth = 2;
      }
    };

    // 초기 그리기 (약간의 지연을 두어 DOM이 완전히 렌더링된 후 실행)
    setTimeout(drawDividers, 100);

    // 리사이즈 이벤트 리스너
    window.addEventListener("resize", drawDividers);
    return () => window.removeEventListener("resize", drawDividers);
  }, []);

  // 마우스/터치 시작
  const startDrawing = (e) => {
    setIsDrawing(true);

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;

    // 시작점 저장
    lastPointRef.current = { x, y };

    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  // 마우스/터치 이동
  const draw = (e) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;

    // 이전 점과 현재 점 사이의 거리 계산
    const dx = x - lastPointRef.current.x;
    const dy = y - lastPointRef.current.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // 경로 길이 누적
    totalPathLengthRef.current += distance;

    // 최소 길이를 넘으면 서명이 있다고 판단
    if (totalPathLengthRef.current >= MIN_SIGNATURE_LENGTH) {
      setHasSignature(true);
    }

    // 현재 점을 이전 점으로 저장
    lastPointRef.current = { x, y };

    const ctx = canvas.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  // 마우스/터치 종료
  const stopDrawing = () => {
    setIsDrawing(false);
  };

  // 다시 서명하기
  const handleClear = () => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      // 전체 캔버스 지우기 (실제 픽셀 크기 기준)
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 스케일 재설정
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);

      // 구분선 다시 그리기 (표시 크기 기준)
      ctx.strokeStyle = "#e5e5e5";
      ctx.lineWidth = 1;
      const thirdWidth = rect.width / 3;
      const padding = 30; // 위아래 여백
      ctx.beginPath();
      ctx.moveTo(thirdWidth, padding);
      ctx.lineTo(thirdWidth, rect.height - padding);
      ctx.moveTo(thirdWidth * 2, padding);
      ctx.lineTo(thirdWidth * 2, rect.height - padding);
      ctx.stroke();

      // 그리기 설정 복원
      ctx.strokeStyle = "#000";
      ctx.lineWidth = 2;
    }
    setHasSignature(false);
    totalPathLengthRef.current = 0;
    lastPointRef.current = { x: 0, y: 0 };
  };

  // applicationId 가져오기
  // const getApplicationId = async () => {
  //   try {
  //     const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
  //     if (!token) {
  //       throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
  //     }
  //
  //     const response = await fetch(`${API_ENDPOINTS.BASE_URL}/clients/me`, {
  //       method: "GET",
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: `Bearer ${token}`,
  //       },
  //     });
  //
  //     if (!response.ok) {
  //       throw new Error("사용자 정보를 가져오는데 실패했습니다.");
  //     }
  //
  //     const data = await response.json();
  //
  //     // applications 배열의 마지막 요소의 id 가져오기
  //     if (data.applications && data.applications.length > 0) {
  //       const lastApplication = data.applications[data.applications.length - 1];
  //       return lastApplication.id;
  //     }
  //
  //     throw new Error("신청서를 찾을 수 없습니다.");
  //   } catch (error) {
  //     console.error("applicationId 가져오기 오류:", error);
  //     throw error;
  //   }
  // };

  // 확인하기
  const handleConfirm = async () => {
    if (!hasSignature) {
      alert("서명을 완료해주세요.");
      return;
    }

    setIsUploading(true);

    try {
      // 1. applicationNumber는 URL 파라미터에서 가져옴
      if (!applicationNumber) {
        throw new Error("신청서 번호를 가져오는데 실패했습니다.");
      }

      // 2. 캔버스에서 이미지 추출 (구분선 제외, 투명 배경)
      const canvas = canvasRef.current;
      if (!canvas) {
        throw new Error("서명 데이터를 가져올 수 없습니다.");
      }

      // 구분선을 제외한 새로운 캔버스 생성
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = rect.width * dpr;
      tempCanvas.height = rect.height * dpr;
      const tempCtx = tempCanvas.getContext("2d");

      // 원본 캔버스의 내용을 복사 (투명 배경 유지)
      tempCtx.drawImage(canvas, 0, 0);

      // 구분선 부분을 투명하게 만들기
      const thirdWidth = (rect.width * dpr) / 3;
      const padding = 30 * dpr;
      const lineWidth = 3 * dpr; // 구분선 제거 영역 (원본은 1px이지만 여유를 두어 더 넓게 제거)

      // 첫 번째 구분선 제거 (사각형 영역으로 제거)
      tempCtx.globalCompositeOperation = "destination-out";
      tempCtx.fillStyle = "rgba(0,0,0,1)";
      tempCtx.fillRect(
        thirdWidth - lineWidth / 2,
        padding,
        lineWidth,
        rect.height * dpr - padding * 2,
      );

      // 두 번째 구분선 제거 (사각형 영역으로 제거)
      tempCtx.fillRect(
        thirdWidth * 2 - lineWidth / 2,
        padding,
        lineWidth,
        rect.height * dpr - padding * 2,
      );

      // composite operation 복원
      tempCtx.globalCompositeOperation = "source-over";

      // 구분선 없이 서명만 있는 캔버스를 Blob으로 변환
      tempCanvas.toBlob(async (blob) => {
        if (!blob) {
          setIsUploading(false);
          throw new Error("서명 이미지를 생성할 수 없습니다.");
        }

        try {
          const formData = new FormData();
          formData.append("file", blob, "signature.png");

          // 서명 이미지 업로드
          // PUT /applications/{applicationNumber}/documents/{documentType}
          const uploadUrl = `${API_ENDPOINTS.BASE_URL}/applications/${applicationNumber}/documents/signature`;

          const response = await fetch(uploadUrl, {
            method: "PUT",
            body: formData,
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || "서명 업로드에 실패했습니다.");
          }

          // 업로드 성공 후 최신 데이터 조회
          try {
            // sessionStorage에서 기존 데이터 가져오기
            const storedData = sessionStorage.getItem("applicationData");
            if (storedData) {
              const existingData = JSON.parse(storedData);
              const phoneNumber =
                existingData.phoneNumber || existingData.client?.phoneNumber;
              const accessCode = existingData.accessCode;

              if (phoneNumber && accessCode) {
                const dataResponse = await fetch(
                  `${API_ENDPOINTS.BASE_URL}/applications/verify-access`,
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                      phoneNumber: phoneNumber,
                      accessCode: accessCode,
                      lawsuitId: 2,
                    }),
                  },
                );

                if (dataResponse.ok) {
                  const latestData = await dataResponse.json();
                  // 응답 데이터를 세션 스토리지에 저장
                  sessionStorage.setItem(
                    "applicationData",
                    JSON.stringify(latestData),
                  );
                }
              }
            }
          } catch (err) {
            console.error("최신 데이터 조회 중 오류:", err);
            // 에러가 발생해도 기존 데이터로 진행
          }

          // 4. 업로드 성공 시 상태 페이지로 돌아가기
          // replace: true로 하면 뒤로가기 시 이전 페이지로 돌아가지 않음
          navigate("/status", { replace: true });
        } catch (error) {
          console.error("서명 업로드 오류:", error);
          alert(error.message || "서명 업로드 중 오류가 발생했습니다.");
          setIsUploading(false);
        }
      }, "image/png");
    } catch (error) {
      console.error("서명 처리 오류:", error);
      alert(error.message || "서명 처리 중 오류가 발생했습니다.");
      setIsUploading(false);
    }
  };

  return (
    <PageBackground>
      <Container>
        <Content>
          <Title>전자서명</Title>
          <Instruction>서명패드에 성함을 정자로 서명해주세요.</Instruction>

          <InfoBox>
            <InfoIcon>ⓘ</InfoIcon>
            <InfoText>성함을 한칸에 한 글자씩 정자로 서명해주세요.</InfoText>
          </InfoBox>

          <SignaturePadContainer>
            <SignatureCanvas
              ref={canvasRef}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={(e) => {
                e.preventDefault();
                startDrawing(e);
              }}
              onTouchMove={(e) => {
                e.preventDefault();
                draw(e);
              }}
              onTouchEnd={stopDrawing}
            />
          </SignaturePadContainer>

          <ButtonContainer>
            <ClearButton onClick={handleClear}>다시 서명하기</ClearButton>
            <ConfirmButton
              onClick={handleConfirm}
              disabled={!hasSignature || isUploading}
            >
              {isUploading ? "업로드 중..." : "확인하기"}
            </ConfirmButton>
          </ButtonContainer>
        </Content>
      </Container>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(180deg, #f6f8ff 0%, #ffffff 100%);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    background: linear-gradient(180deg, #f6f8ff 0%, #ffffff 100%);
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: transparent;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: 100vh;
    box-shadow: none;
    padding: 0 0 100px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 60px 20px 80px;
    margin-top: 40px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 10px;
  color: #333;
  text-align: left;
  width: 100%;
  align-self: flex-start;
`;

const Instruction = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.4;
  text-align: left;
  width: 100%;
`;

const InfoBox = styled.div`
  width: 100%;
  background-color: #f5f5f5;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 30px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const InfoIcon = styled.span`
  color: #0075ff;
  margin-top: 2px;
  font-size: 16px;
  margin-right: 0px;
  font-weight: bold;
`;

const InfoText = styled.p`
  font-size: 14px;
  color: #333;
  line-height: 1.5;
  margin: 0;
  flex: 1;
`;

const SignaturePadContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 40px;
  padding: 0 10px;
`;

const SignatureCanvas = styled.canvas`
  width: 100%;
  max-width: 600px;
  height: 180px;
  border: 2px solid #e5e5e5;
  border-radius: 12px;
  background-color: white;
  touch-action: none;
  cursor: crosshair;
`;

const ButtonContainer = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
`;

const ClearButton = styled(motion.button)`
  width: 100%;
  height: 56px;
  padding: 0 14px;
  border-radius: 20px;
  background-color: white;
  border: 1px solid #e5e5e5;
  color: #333;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f9f9f9;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ConfirmButton = styled(motion.button)`
  width: 100%;
  height: 56px;
  padding: 0 14px;
  border-radius: 20px;
  background-color: ${(props) => (props.disabled ? "#A9DAFF" : "#3855E2")};
  border: none;
  color: ${(props) => (props.disabled ? "#CCEAFF" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  opacity: ${(props) => (props.disabled ? 1 : 1)};
  pointer-events: ${(props) => (props.disabled ? "auto" : "auto")};

  &:hover {
    background-color: ${(props) => (props.disabled ? "#A9DAFF" : "#2d44b8")};
  }

  &:active {
    transform: scale(0.98);
  }
`;

export default StatusSignaturePadPage;
